import {
  loadNostoPlacements,
  nostoHasProducts,
  bindNostoNativeAddToCart,
  bindTileSwatchSwap,
  fetchThemeTileData,
  dedupeNostoOptionIds,
  fixNostoSizeLabels,
  nostoSizeOptionIndex,
  observeNostoExclusions,
  observeNostoPriceFormatting,
  productHandleFromHref,
} from '@theme/nosto-recommendations';
import { SINGLE_UNIT_HANDLES } from '@theme/cart-app-sync';

/** @type {WeakSet<Element>} */
const initializedRoots = new WeakSet();

/** @type {WeakSet<Element>} */
const loadingRoots = new WeakSet();

/** @type {ReturnType<typeof setTimeout> | null} */
let loadTimer = null;

/* The cart changed since the row was last loaded, so its picks are for the old cart. */
let cartChanged = false;
/* Survives the debounce, so a later plain schedule can't drop a pending refresh. */
let pendingRefresh = false;
/* Products the row's picks were loaded for; null when unknown. */
let picksFor = null;

/**
 * Sorted product handles in the cart, without add-ons: quantity changes and add-on toggles keep the picks.
 * @param {Array<string | null | undefined>} handles
 */
function productKey(handles) {
  return [...new Set(handles)]
    .filter((handle) => handle && !SINGLE_UNIT_HANDLES.includes(handle))
    .sort()
    .join(',');
}

function drawerProductKey() {
  return productKey(
    Array.from(document.querySelectorAll('cart-drawer-component [ref="cartItemRows[]"][data-handle]'), (row) => row.getAttribute('data-handle'))
  );
}

function getRecsRoots() {
  return Array.from(document.querySelectorAll('[data-cart-nosto-recs]'));
}

/**
 * @param {number} [timeoutMs]
 * @returns {Promise<Element[]>}
 */
function waitForRecsRoots(timeoutMs = 10000) {
  return new Promise((resolve) => {
    const immediate = getRecsRoots();
    if (immediate.length) return resolve(immediate);

    const observer = new MutationObserver(() => {
      const roots = getRecsRoots();
      if (!roots.length) return;
      observer.disconnect();
      clearTimeout(timer);
      resolve(roots);
    });
    observer.observe(document.body, { childList: true, subtree: true });

    const timer = setTimeout(() => {
      observer.disconnect();
      resolve(getRecsRoots());
    }, timeoutMs);
  });
}

function getPlacementId(recsRoot) {
  return (
    recsRoot?.getAttribute('data-nosto-placement')?.trim() ||
    recsRoot?.querySelector('.nosto_element')?.id ||
    'nosto-mini-cart-recs'
  );
}

/**
 * @param {Element} el
 * @param {string} name
 * @param {string} value
 */
function setAttr(el, name, value) {
  if (el.getAttribute(name) !== value) el.setAttribute(name, value);
}

/**
 * @param {Element} el
 * @param {string} name
 */
function removeAttr(el, name) {
  if (el.hasAttribute(name)) el.removeAttribute(name);
}

function markFilled(recsRoot) {
  if (!(recsRoot instanceof HTMLElement)) return;
  removeAttr(recsRoot, 'data-nosto-empty');
  setAttr(recsRoot, 'data-nosto-loaded', '');
  setAttr(recsRoot, 'aria-hidden', 'false');
}

function syncVisibility(recsRoot, nostoContainer) {
  if (!(recsRoot instanceof HTMLElement) || !nostoContainer) return;
  if (nostoHasProducts(nostoContainer)) {
    markFilled(recsRoot);
    return;
  }
  /* Keep filled after cart morph so the section does not collapse. */
  if (recsRoot.hasAttribute('data-nosto-loaded')) {
    removeAttr(recsRoot, 'data-nosto-empty');
    setAttr(recsRoot, 'aria-hidden', 'false');
    return;
  }
  setAttr(recsRoot, 'data-nosto-empty', '');
  setAttr(recsRoot, 'aria-hidden', 'true');
}

/*
 * Nosto's tile carries its own product name (a separate Nosto field, not the theme's
 * `short_title`) and, for colours, at most a count or a single guessed dot, never the
 * product's colour family. So both are replaced with what every other
 * product card on the site shows: the title and swatches the theme itself renders for
 * that product, fetched once per handle by fetchThemeTileData (assets/nosto-recommendations.js).
 */

/**
 * @typedef {import('@theme/nosto-recommendations').TileProduct} TileProduct
 * @typedef {import('@theme/nosto-recommendations').TileData} TileData
 */

/**
 * @param {Element} tile - a Nosto `.product-tile`
 * @param {TileData | null} data
 */
function applyThemeTileData(tile, data) {
  if (data?.title) {
    /* Nosto prints an EN and an FR span and hides one, but not during a re-render; one text node can't show twice. */
    const link = tile.querySelector('.product-tile__title a');
    if (link) link.textContent = data.title;
  }

  /* Nosto's colour element is a link per locale; swatches are links too, so they can't nest in it. */
  const nostoColours = tile.querySelectorAll('.product-tile__colors');
  const colours = document.createElement('div');
  colours.className = 'product-tile__colors';
  if (data?.swatches) colours.append(data.swatches.cloneNode(true));
  if (nostoColours.length) {
    nostoColours[0].replaceWith(colours);
    nostoColours.forEach((el, index) => index > 0 && el.remove());
  } else {
    (tile.querySelector('.product-tile__details') || tile).append(colours);
  }
}

/**
 * @param {Element} container - the `.nosto_element` placement
 */
function syncThemeTileData(container) {
  container.querySelectorAll('.product-tile').forEach((tile) => {
    if (tile.hasAttribute('data-theme-tile-data')) return;
    /* Set before the fetch, so the observer firing on our own writes can't start a second pass. */
    tile.setAttribute('data-theme-tile-data', 'pending');

    const link = tile.querySelector('.product-tile__title a[href], a.product-tile__link[href]');
    const handle = productHandleFromHref(link?.getAttribute('href') ?? null);
    const done = (data) => {
      if (!tile.isConnected) return;
      applyThemeTileData(tile, data);
      tile.setAttribute('data-theme-tile-data', data ? 'done' : 'failed');
    };
    if (!handle) return done(null);
    fetchThemeTileData(handle).then(done);
  });
}

/*
 * Clicking a sibling colour's swatch switches the tile to that colour in place, the way a
 * PLP card does (assets/color-swatch-card.js). That script cannot do it here: it swaps a
 * theme `<product-card>`'s gallery and quick-add form, and a Nosto tile has neither, so
 * the swatch link just navigated to the PDP. Everything Nosto rendered for its original
 * product is rewritten from the sibling's tile data instead - including the size panel's
 * radios and the submit button's product, which are what reach the cart.
 */

/**
 * Rebuilds a tile's size panel for another product, reusing one of Nosto's own rows as
 * the template so its classes and the shared `.nosto_element` panel styling carry over.
 * @param {Element} tile
 * @param {TileProduct} product
 * @returns {boolean} whether the panel (if the tile has one) now points at `product`
 */
function swapTileQuickAdd(tile, product) {
  const wrapper = tile.querySelector('.product-tile__quick-add');
  if (!wrapper) return true;

  const panel = wrapper.querySelector('.product-quick-add');
  const group = panel?.querySelector('.options-group');
  const template = group?.querySelector('.options-group__list');
  const submits = wrapper.querySelectorAll('.button--quick-add-submit');
  if (!(panel instanceof HTMLElement) || !group || !template || !submits.length || !product.variants?.length) {
    return false;
  }

  /* No size option (a one-size product): Nosto prints the variant title, so do the same. */
  const sizeIndex = nostoSizeOptionIndex(product.options ?? []);

  const rows = product.variants.map((variant, index) => {
    const row = /** @type {Element} */ (template.cloneNode(true));
    const radio = row.querySelector('input[type="radio"]');
    const label = row.querySelector('.options-group__label');
    if (!(radio instanceof HTMLInputElement) || !(label instanceof HTMLLabelElement)) return row;

    row.classList.toggle('over-six', index >= 6);
    radio.value = String(variant.id);
    radio.checked = false;
    radio.classList.toggle('product-unavailable', !variant.available);
    label.classList.toggle('is-disabled', !variant.available);
    label.removeAttribute('aria-busy');
    const text = document.createElement('span');
    text.textContent = (sizeIndex >= 0 ? variant.options?.[sizeIndex] : null) ?? variant.title;
    label.replaceChildren(text);
    return row;
  });
  group.replaceChildren(...rows);
  group.classList.toggle('over-six-size-options', rows.length > 6);
  fixNostoSizeLabels(group);

  /*
   * Every row is a clone of one Nosto row, so they all share its id until the dedupe pass
   * gives them their own. Run it now rather than leaving it to the "+" click: the panel
   * may already be open, and the click is the only thing that runs it.
   */
  delete panel.dataset.nostoOptionIdsUnique;
  dedupeNostoOptionIds(panel);
  /* The rows already carry the product's real sizes, so the relabel pass has nothing to fix. */
  panel.dataset.nostoSizesRelabelled = 'true';

  const firstAvailable = product.variants.find((variant) => variant.available) ?? product.variants[0];
  submits.forEach((submit) => {
    if (!(submit instanceof HTMLElement)) return;
    submit.id = String(product.id);
    submit.dataset.product = String(product.id);
    submit.dataset.variant = String(firstAvailable.id);
    submit.dataset.name = product.name;
    submit.dataset.url = product.url;
    submit.dataset.handle = product.handle;
  });
  return true;
}

/**
 * @param {Element} tile
 * @param {TileData} data - with a `product`
 */
function swapTileProduct(tile, data) {
  const product = /** @type {TileProduct} */ (data.product);

  tile.querySelectorAll('a.product-tile__link').forEach((link) => {
    if (!(link instanceof HTMLAnchorElement)) return;
    link.href = product.url;
    if (link.hasAttribute('title')) link.title = product.name;
  });

  tile.querySelectorAll('.product-tile__image-wrapper img').forEach((img) => {
    if (!(img instanceof HTMLImageElement)) return;
    if (product.image?.src) {
      img.srcset = product.image.srcset || '';
      img.src = product.image.src;
    }
    img.alt = product.name;
  });

  const values = tile.querySelector('.product-tile__pricing .pricing__values');
  if (values && data.pricing) values.replaceChildren(...[...data.pricing.cloneNode(true).childNodes]);

  /* Title and swatches, and with them the selected ring on the sibling's own dot. */
  applyThemeTileData(tile, data);
}

/**
 * @param {HTMLElement} tile
 * @param {TileData} data - with a `product`
 */
function swapTileColour(tile, data) {
  if (!data.product || !swapTileQuickAdd(tile, data.product)) return false;
  swapTileProduct(tile, data);
  return true;
}

/**
 * Lines the title's text actually wraps to, unclamped (clamped lines still lay out).
 * @param {Element} title
 */
function titleLineCount(title) {
  const walker = document.createTreeWalker(title, NodeFilter.SHOW_TEXT);
  const range = document.createRange();
  /** @type {number[]} */
  const tops = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    range.selectNodeContents(node);
    for (const rect of range.getClientRects()) {
      if (!rect.width || !rect.height) continue;
      if (!tops.some((top) => Math.abs(top - rect.top) < rect.height / 2)) tops.push(rect.top);
    }
  }
  return tops.length;
}

/*
 * Titles reserve a fixed height so price and swatches line up across the row; reserve
 * two lines only when some title in the row wraps, so one-line rows don't leave a gap.
 */
function syncTitleLines(container) {
  const lines = Math.max(0, ...Array.from(container.querySelectorAll('.product-tile__title'), titleLineCount));
  if (!lines) return;
  const value = String(Math.min(lines, 2));
  if (container.style.getPropertyValue('--cart-nosto-title-lines') !== value) {
    container.style.setProperty('--cart-nosto-title-lines', value);
  }
}

function initCarouselUi(recsRoot) {
  if (!recsRoot || initializedRoots.has(recsRoot)) return;
  const nostoContainer = recsRoot.querySelector('.nosto_element');
  if (!nostoContainer) return;

  initializedRoots.add(recsRoot);
  /*
   * Register the exclusion observer before any other observer on this container: they
   * fire in registration order, so pruning first means the visibility sync, swatch pass
   * and arrow measurement below all see the filtered set.
   */
  observeNostoExclusions(nostoContainer);
  observeNostoPriceFormatting(nostoContainer);
  bindNostoNativeAddToCart(nostoContainer);
  bindTileSwatchSwap(nostoContainer, '.product-tile', swapTileColour);

  const arrows = recsRoot.querySelector('.nosto-carousel__arrows');
  const prevBtn = recsRoot.querySelector('[data-nosto-scroll="prev"]');
  const nextBtn = recsRoot.querySelector('[data-nosto-scroll="next"]');
  /** @type {Element | null} */
  let boundScrollEl = null;

  const scrollTarget = () => nostoContainer.querySelector('.swiper-wrapper') || nostoContainer;

  const scrollByCard = (direction) => {
    const target = scrollTarget();
    if (!target) return;
    const slide = target.querySelector('.swiper-slide, .product-tile');
    const gap = parseFloat(getComputedStyle(target).gap) || 0;
    const amount = slide ? slide.getBoundingClientRect().width + gap : Math.max(target.clientWidth * 0.8, 120);
    const nextLeft = target.scrollLeft + (direction === 'next' ? amount : -amount);
    if (typeof target.scrollTo === 'function') {
      target.scrollTo({ left: nextLeft, behavior: 'smooth' });
    } else {
      target.scrollLeft = nextLeft;
    }
    requestAnimationFrame(updateArrows);
  };

  const updateArrows = () => {
    const target = scrollTarget();
    const max = target.scrollWidth - target.clientWidth;
    /* Every tile already fits: nothing to slide to, so no arrows at all. */
    if (arrows instanceof HTMLElement) arrows.hidden = max <= 1;
    if (prevBtn) prevBtn.disabled = target.scrollLeft <= 1;
    if (nextBtn) nextBtn.disabled = max <= 1 || target.scrollLeft >= max - 1;
  };

  const bindScroll = () => {
    const target = scrollTarget();
    if (boundScrollEl === target) return;
    if (boundScrollEl) boundScrollEl.removeEventListener('scroll', updateArrows);
    boundScrollEl = target;
    boundScrollEl.addEventListener('scroll', updateArrows, { passive: true });
  };

  prevBtn?.addEventListener('click', () => scrollByCard('prev'));
  nextBtn?.addEventListener('click', () => scrollByCard('next'));

  let refreshQueued = false;
  // Measures once per frame, however many mutations land; measuring per mutation forced a layout each time.
  const refreshArrows = () => {
    if (refreshQueued) return;
    refreshQueued = true;
    requestAnimationFrame(() => {
      refreshQueued = false;
      syncTitleLines(nostoContainer);
      bindScroll();
      updateArrows();
      requestAnimationFrame(updateArrows);
    });
  };

  const onInject = () => {
    /* Reveal before measuring arrows — empty CSS clamps height. */
    syncVisibility(recsRoot, nostoContainer);
    syncThemeTileData(nostoContainer);
    fixNostoSizeLabels(nostoContainer);
    refreshArrows();
  };

  new MutationObserver(onInject).observe(nostoContainer, { childList: true, subtree: true });
  /* Morph restores the server-rendered `data-nosto-empty` on every cart update. */
  new MutationObserver(() => syncVisibility(recsRoot, nostoContainer)).observe(recsRoot, {
    attributes: true,
    attributeFilter: ['data-nosto-empty'],
  });
  if (typeof ResizeObserver === 'function') {
    new ResizeObserver(refreshArrows).observe(nostoContainer);
  }
  onInject();
}

/**
 * @param {Element} target
 * @param {number} [timeoutMs]
 * @returns {Promise<boolean>}
 */
function waitForInjection(target, timeoutMs = 2500) {
  return new Promise((resolve) => {
    if (nostoHasProducts(target)) return resolve(true);

    const observer = new MutationObserver(() => {
      if (!nostoHasProducts(target)) return;
      observer.disconnect();
      clearTimeout(timer);
      resolve(true);
    });
    observer.observe(target, { childList: true, subtree: true });

    const timer = setTimeout(() => {
      observer.disconnect();
      resolve(nostoHasProducts(target));
    }, timeoutMs);
  });
}

/**
 * @param {{ refresh?: boolean, delay?: number }} [options] - refresh re-requests rows that are already filled
 */
function schedulePlacementLoad(options = {}) {
  if (options.refresh) pendingRefresh = true;
  if (loadTimer) clearTimeout(loadTimer);
  loadTimer = setTimeout(async () => {
    loadTimer = null;
    const refresh = pendingRefresh;
    pendingRefresh = false;

    const roots = await waitForRecsRoots();
    if (!roots.length) return;

    roots.forEach((root) => {
      if (root instanceof HTMLElement) initCarouselUi(root);
    });

    let requested = false;

    for (const root of roots) {
      if (!(root instanceof HTMLElement)) continue;
      const target = root.querySelector('.nosto_element');
      const placementId = getPlacementId(root);
      const alreadyFilled = target && nostoHasProducts(target);
      picksFor ??= drawerProductKey();
      if (alreadyFilled && !refresh) {
        markFilled(root);
        continue;
      }

      if (loadingRoots.has(root)) continue;
      loadingRoots.add(root);

      try {
        if (target instanceof HTMLElement) {
          cartChanged = false;
          requested = true;
          await loadNostoPlacements({ [placementId]: target }, { resendCartTagging: true, replace: refresh });
        }

        if (target && !(await waitForInjection(target))) {
          /* Drawer re-rendered mid-request — retry against the live placement. */
          const liveRoot = document.querySelector('[data-cart-nosto-recs]');
          const liveTarget = liveRoot?.querySelector('.nosto_element');

          if (liveRoot instanceof HTMLElement && liveTarget instanceof HTMLElement) {
            initCarouselUi(liveRoot);
            await loadNostoPlacements({ [getPlacementId(liveRoot)]: liveTarget }, { resendCartTagging: true });
            await waitForInjection(liveTarget);
            syncVisibility(liveRoot, liveTarget);
            continue;
          }
        }

        /* No picks for the new cart: let the row collapse rather than hold an empty heading. */
        if (refresh && target && !nostoHasProducts(target)) removeAttr(root, 'data-nosto-loaded');
        if (target) syncVisibility(root, target);
      } finally {
        loadingRoots.delete(root);
      }
    }

    /* A cart update landed mid-request, so these picks are already for the old cart. */
    if (requested && cartChanged) schedulePlacementLoad({ refresh: true });
  }, options.delay ?? 80);
}

function bindLifecycle() {
  /* dialog:open does not bubble — capture on document. */
  if (!document.documentElement.dataset.nostoDrawerBound) {
    document.documentElement.dataset.nostoDrawerBound = 'true';
    document.addEventListener(
      'dialog:open',
      (event) => {
        const isCartDrawer = event.target instanceof Element && event.target.matches('cart-drawer-component');
        schedulePlacementLoad({ refresh: isCartDrawer && cartChanged });
      },
      true
    );
  }

  if (!document.documentElement.dataset.nostoCartBound) {
    document.documentElement.dataset.nostoCartBound = 'true';
    document.addEventListener('cart:update', (event) => {
      const items = /** @type {CustomEvent} */ (event).detail?.resource?.items;
      const key = Array.isArray(items) ? productKey(items.map((item) => item.handle)) : null;
      if (key !== null && key === picksFor) return;
      picksFor = key;
      cartChanged = true;
      schedulePlacementLoad({ refresh: true, delay: 400 });
    });
  }
}

/* Re-bind after the drawer morphs its contents. */
function initVisibleRoots() {
  getRecsRoots().forEach((root) => {
    if (!(root instanceof HTMLElement)) return;
    initCarouselUi(root);
    const nostoContainer = root.querySelector('.nosto_element');
    if (nostoContainer) syncVisibility(root, nostoContainer);
  });
}

function watchForRerender() {
  const drawerRoot = document.querySelector('cart-drawer-component') ?? document.body;
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      initVisibleRoots();
    });
  }).observe(drawerRoot, { childList: true, subtree: true });
}

function boot() {
  bindLifecycle();
  watchForRerender();
  schedulePlacementLoad();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

window.addEventListener('load', () => schedulePlacementLoad());
